import {AsyncThunkActionTypes, SliceStatus} from 'interfaces';
import {
  Connection,
  CONNECTION_TYPE,
  IncomingUserEntity,
  Message,
  MESSAGE_STATUS,
  NormalizedConnections,
} from 'interfaces/Pages/Messaging';
import cloneDeep from 'lodash.clonedeep';

import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit';

import {
  addIncomingMessage,
  addOptimisticMessage,
  cometChatLogOut,
  cometChatLogOutFailure,
  cometChatLogOutSuccess,
  getAConnection,
  getConnections,
  getMessages,
  getUnreadAllMessageCount,
  loginUserOnCometChat,
  markReceivedMessageAsDelivered,
  markReceivedMessageAsRead,
  markSentMessageAsDelivered,
  markSentMessageAsRead,
  resetExternalConnection,
  resetSearchConnection,
  // New changes
  searchConnection,
  searchConnectionFailure,
  searchConnectionSuccess,
  setLastMessage,
  setParticipantId,
  setUnreadMessageCount,
  setUserPresenceStatus,
  setUserTypingStatus,
  updateOptimisticMessage,
} from './messagingActions';
import {MessagingSliceState} from './types';
import {
  transformIncomingMessageToLastMessageShape,
  transformMessageToLastMessageShape,
} from './utils';

/**
 * Initial state of the messaging slice.
 */
export const messagingSliceInitialState: MessagingSliceState = {
  percentUploaded: 0,
  status: SliceStatus.idle,
  error: '',
  cometChatUser: null,
  refreshingState: SliceStatus.idle,
  unreadAllMessageCount: 0,
  loading: SliceStatus.idle,

  // Connections/Networks/Friends
  connections: {
    status: SliceStatus.idle,
    data: null,
  },
  connectionsCopy: null,
  searchConnection: {
    keyword: '',
    status: SliceStatus.idle,
  },

  // Messages
  messages: null,
  conversations: null,
  messageMetadata: {
    status: SliceStatus.idle,
  },

  externalConnection: {
    data: null,
    status: SliceStatus.idle,
  },

  participantId: '',
};

const messagingSlice = createSlice({
  name: 'messaging',
  initialState: messagingSliceInitialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<MessagingSliceState>) =>
    builder
      .addCase(loginUserOnCometChat.pending, state => {
        state.loading = SliceStatus.pending;
      })
      .addCase(loginUserOnCometChat.fulfilled, (state, action) => {
        state.loading = SliceStatus.resolved;
        state.cometChatUser = action.payload ?? null;
      })
      .addCase(loginUserOnCometChat.rejected, state => {
        state.loading = SliceStatus.rejected;
      })
      // eslint-disable-next-line complexity
      .addCase(addIncomingMessage, (state, action) => {
        if (state.connections.status === SliceStatus.idle) {
          return;
        }

        const message = action.payload.message;
        const isChatting = action.payload.isChatting;

        // add message first
        if (!state.messages) state.messages = {};
        state.messages[message.id] = message as unknown as Message;

        // Helper function to add or update a connection
        const addConnection = (
          connections: NormalizedConnections,
          sender: IncomingUserEntity,
        ) => {
          if (!connections) return;

          // Initialize required objects if they don't exist
          if (!connections.entities) {
            connections.entities = {connections: {}};
          }
          if (!connections.nextRequest) {
            connections.nextRequest = {
              type: CONNECTION_TYPE.RECENT,
              meta: {
                count: 0,
                perPage: 0,
                currentPage: 0,
                totalPages: 0,
                nextPage: null,
                hasMore: false,
              },
            };
          }
          if (connections.total === undefined) {
            connections.total = 0;
          }
          if (!connections.entities.connections) {
            connections.entities.connections = {};
          }
          if (!connections.result) {
            connections.result = [];
          }

          // Initialize the connection with sender properties
          const connectionId = sender.uid;
          connections.entities.connections[connectionId] = {
            ...(sender as unknown as Connection),
            lastMessage: transformIncomingMessageToLastMessageShape(message),
            typing: false,
            unreadMessageCount: 1,
            conversationId: message.conversationId,
            isSearched: false,
            isChatActive: true,
          };

          // Add to results array if not already present
          if (!connections.result.includes(connectionId)) {
            connections.result.unshift(connectionId); // Add to beginning
          }
        };

        const isUserViewingSearchedResults =
          state.searchConnection.keyword !== '';

        if (isUserViewingSearchedResults) {
          const connection =
            state.connectionsCopy?.entities?.connections?.[message.sender.uid];
          if (!connection) {
            // add connection to top, increment unread message count and move to top
            addConnection(state.connectionsCopy, message.sender);
          } else if (state.connectionsCopy && connection) {
            // update last message
            connection.lastMessage =
              transformIncomingMessageToLastMessageShape(message);

            // increment unread message count
            connection.unreadMessageCount = connection.unreadMessageCount + 1;

            // move connection to top
            const connectionIdx = state.connectionsCopy.result.indexOf(
              message.sender.uid,
            );
            if (connectionIdx >= 0 && connectionIdx !== 0) {
              state.connectionsCopy.result.splice(connectionIdx, 1);
              state.connectionsCopy.result.unshift(message.sender.uid);
            }

            /**
             * add message to conversation, if user has opened the sender's conversation
             * or if is chatting with the sender
             */
            const conversationMessages =
              state.conversations?.[message.conversationId]?.messages;
            if (conversationMessages) {
              conversationMessages.push(message.id);
            }
          }
        } else {
          const connection =
            state.connections?.data?.entities?.connections?.[
              message.sender.uid
            ];
          if (!connection) {
            if (state.connections.data) {
              // Use the addConnection function which now directly mutates
              addConnection(state.connections.data, message.sender);
            }
          } else if (state.connections.data && connection) {
            if (!isChatting) {
              // increment unread message count
              connection.unreadMessageCount = connection.unreadMessageCount + 1;
            }

            // update last message
            connection.lastMessage =
              transformIncomingMessageToLastMessageShape(message);

            // move connection to top
            const connIdx = state.connections.data.result.indexOf(
              connection.uid,
            );
            if (connIdx >= 0 && connIdx !== 0) {
              state.connections.data.result.splice(connIdx, 1);
              state.connections.data.result.unshift(connection.uid);
            }

            /**
             * add message to conversation, if user has opened the sender's conversation
             * or if is chatting with the sender
             */
            const conversationMessages =
              state.conversations?.[message.conversationId]?.messages;
            if (conversationMessages) {
              conversationMessages.push(message.id);
            }
          }
        }
      })
      // eslint-disable-next-line complexity
      .addCase(addOptimisticMessage, (state, action) => {
        const message = action.payload;
        const receiverId = message.receiver;
        const senderId = message.sender;

        if (!message.tempId) {
          return;
        }
        if (!state.messages) state.messages = {};
        state.messages[message.tempId] = {
          ...message,
          sender: senderId,
          receiver: receiverId,
        } as unknown as Message;

        const conversation = state.conversations?.[message.conversationId];
        if (conversation) {
          conversation.messages.push(message.tempId);
        }

        // set last message && move connection to top
        const isUserViewingSearchedConnections =
          state.searchConnection.keyword !== '';
        const backupConnection =
          state.connectionsCopy?.entities?.connections?.[receiverId];

        if (isUserViewingSearchedConnections) {
          const connection = state.connections?.data?.entities?.connections?.[
            receiverId
          ] as Connection;

          if (!backupConnection) {
            /**
             * Since this is a searched user, so add it first in the
             * main connections list i.e. `connectionsCopy`
             */
            if (!state.connectionsCopy)
              state.connectionsCopy = {
                entities: {connections: {}},
                result: [],
                nextRequest: {
                  type: CONNECTION_TYPE.RECENT,
                  meta: {
                    count: 0,
                    perPage: 0,
                    currentPage: 0,
                    totalPages: 0,
                    nextPage: null,
                    hasMore: false,
                  },
                },
                total: 0,
              };
            if (!state.connectionsCopy.entities)
              state.connectionsCopy.entities = {connections: {}};
            if (!state.connectionsCopy.entities.connections)
              state.connectionsCopy.entities.connections = {};

            // Set the connection directly
            state.connectionsCopy.entities.connections[receiverId] = {
              ...connection,
              lastMessage: transformMessageToLastMessageShape(message),
              typing: false,
            };

            // Add receiverId to the beginning of the result array if not already present
            if (!state.connectionsCopy.result)
              state.connectionsCopy.result = [];
            if (!state.connectionsCopy.result.includes(receiverId)) {
              state.connectionsCopy.result.unshift(receiverId);
            }
          } else {
            backupConnection.lastMessage = transformMessageToLastMessageShape(
              action.payload,
            );
            const backupConnectionIdx =
              state.connectionsCopy?.result.indexOf(receiverId);
            if (
              backupConnectionIdx !== undefined &&
              backupConnectionIdx >= 0 &&
              backupConnectionIdx !== 0
            ) {
              state.connectionsCopy?.result.splice(backupConnectionIdx, 1);
              state.connectionsCopy?.result.unshift(receiverId);
            }
          }

          connection.lastMessage = transformMessageToLastMessageShape(
            action.payload,
          );
        } else {
          const connection =
            state.connections?.data?.entities?.connections?.[receiverId];
          /**
           * update last message and
           * if user(connection) already exists then move to top in the connections list
           */
          if (state.connections.data && connection) {
            connection.lastMessage = transformMessageToLastMessageShape(
              action.payload,
            );

            const connectionIdx =
              state.connections.data.result.indexOf(receiverId);
            if (connectionIdx >= 0 && connectionIdx !== 0) {
              state.connections.data.result.splice(connectionIdx, 1);
              state.connections.data.result.unshift(receiverId);
            }
          }
        }
      })
      // eslint-disable-next-line complexity
      .addCase(updateOptimisticMessage, (state, action) => {
        const message = action.payload;

        if (!message.tempId) {
          return;
        }

        // Initialize messages object if it doesn't exist
        if (!state.messages) {
          state.messages = {};
        }

        if (
          message.status !== undefined &&
          (message.status === MESSAGE_STATUS.FAILED ||
            message.status === MESSAGE_STATUS.SENDING)
        ) {
          // Update status directly
          state.messages[message.tempId].status = message.status;
        } else {
          if (!message.id) {
            return;
          }
          // Add message with actual ID as a record key
          if (!state.messages[message.id]) {
            // Create new message object
            state.messages[message.id] = message as unknown as Message;
          } else {
            // Update existing message object's properties
            Object.keys(message).forEach(key => {
              // Type-safe property access with key as keyof
              const typedKey = key as keyof typeof message;
              if (message[typedKey] !== undefined) {
                // Safe assignment with proper typing
                if (state.messages && message.id) {
                  (state.messages[message.id] as any)[typedKey] =
                    message[typedKey];
                }
              }
            });
          }

          // Remove the temporary message if it exists
          if (state.messages[message.tempId]) {
            delete state.messages[message.tempId];
          }

          if (message.conversationId) {
            // Ensure state.conversations exists and update the conversation's message list
            const conversation = state.conversations?.[message.conversationId];
            if (conversation) {
              const msgIndex = conversation.messages.indexOf(message.tempId);
              if (msgIndex !== -1) {
                conversation.messages[msgIndex] = message.id; // Replace tempId with actual ID
              }
            }
          }
        }
      })
      .addCase(setLastMessage, (state, action) => {
        const {uid, data} = action.payload;
        const connection = state.connections.data?.entities.connections[uid];

        if (connection) {
          connection.lastMessage = data;
        }
      })
      .addCase(setUserPresenceStatus, (state, action) => {
        const {uid, status, lastActiveAt} = action.payload;

        const connection = state.connections.data?.entities.connections[uid];

        if (connection) {
          connection.lastActiveAt = lastActiveAt;
          connection.status = status;
        }
      })
      .addCase(setUserTypingStatus, (state, action) => {
        const {uid, typing} = action.payload;
        const connection = state.connections.data?.entities.connections[uid];

        if (connection) {
          connection.typing = typing;
        }
      })
      // sends by comet-chat listener, when receiver sees the message
      .addCase(markSentMessageAsRead, (state, action) => {
        const {messageId, readAt} = action.payload;
        const message = state.messages?.[messageId];
        if (state.messages && message) {
          message.readAt = readAt;
        }
      })
      .addCase(markReceivedMessageAsRead, (state, action) => {
        const {messageId, readAt, senderId} = action.payload;
        const message = state.messages?.[messageId];
        if (state.messages && message) {
          message.readAt = readAt;
        }

        const connection =
          state.connections?.data?.entities?.connections?.[senderId];
        if (connection && Number(connection.unreadMessageCount) > 0) {
          connection.unreadMessageCount = connection.unreadMessageCount - 1;
        }
      })
      // sends by comet-chat listener, when receiver received the message
      .addCase(markSentMessageAsDelivered, (state, action) => {
        const {messageId, deliveredAt} = action.payload;
        const message = state.messages?.[messageId];
        if (state.messages && message) {
          message.deliveredAt = deliveredAt;
        }
      })
      .addCase(markReceivedMessageAsDelivered, (state, action) => {
        const {messageId, deliveredAt} = action.payload;
        const message = state.messages?.[messageId];
        if (state.messages && message) {
          message.deliveredAt = deliveredAt;
        }
      })
      .addCase(getMessages.pending, state => {
        state.messageMetadata.status = SliceStatus.pending;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        const {conversationId, firstCall} = action.meta.arg;
        const {result, hasMore, lastMessageId, entities} = action.payload;

        const existingConversation = state.conversations?.[conversationId];

        const updatedMessages =
          firstCall || !existingConversation
            ? result
            : Array.from(
                new Set([...result, ...existingConversation.messages]),
              );

        state.conversations = {
          ...state.conversations,
          [conversationId]: {
            id: conversationId,
            messages: updatedMessages,
            hasMore,
            lastMessageId,
          },
        };

        state.messages = {
          ...state.messages,
          ...entities.messages,
        };

        state.messageMetadata.status = SliceStatus.resolved;
      })
      .addCase(getMessages.rejected, state => {
        state.messageMetadata.status = SliceStatus.rejected;
      })
      .addCase(getConnections.pending, (state, action) => {
        const payload = {
          ...state,
          connections: {
            ...state.connections,
            status: SliceStatus.pending,
          },
        };
        // If it's a first API call
        if (
          action.meta.arg?.type === CONNECTION_TYPE.RECENT &&
          action.meta.arg?.page === 1
        ) {
          payload.connections = {
            ...payload.connections,
            data: null,
          };
        }

        if (action.meta.arg?.refresh) {
          payload.connections = {
            ...payload.connections,
            data: null,
          };
        }

        return payload;
      })
      .addCase(getConnections.fulfilled, (state, {payload, meta}) => {
        state.connections.status = SliceStatus.resolved;

        if (meta.arg?.refresh) {
          state.refreshingState = SliceStatus.resolved;
        }

        if (payload) {
          state.connections.data = {
            ...payload,
            entities: {
              connections: {
                ...state.connections.data?.entities?.connections,
                ...payload?.entities?.connections,
              },
            },
            result: [
              ...(state.connections.data?.result ?? []),
              ...payload.result,
            ],
          };
        }
      })
      .addCase(getConnections.rejected, (state, action) => {
        state.connections.status = SliceStatus.rejected;

        if (action.meta.arg?.refresh) {
          state.refreshingState = SliceStatus.resolved;
        }
      })
      .addCase(getAConnection.pending, state => {
        state.externalConnection.status = SliceStatus.pending;
      })
      .addCase(getAConnection.fulfilled, (state, {payload}) => {
        if (payload) {
          state.externalConnection = {
            data: payload,
            status: SliceStatus.resolved,
          };
        }
      })
      .addCase(getAConnection.rejected, state => {
        state.externalConnection.status = SliceStatus.rejected;
      })
      .addCase(resetExternalConnection, state => {
        state.externalConnection.status = SliceStatus.idle;
        state.externalConnection.data = null;
      })
      .addCase(searchConnection, (state, action) => {
        const payload = {
          ...state,
          searchConnection: {
            keyword: action.payload.keyword,
            status: SliceStatus.pending,
          },
        };
        return payload;
      })
      .addCase(searchConnectionSuccess, (state, {payload}) => {
        state.connections.status = SliceStatus.resolved;
        state.searchConnection.status = SliceStatus.resolved;

        if (state.connectionsCopy === null) {
          if (state.connections.data !== null) {
            state.connectionsCopy = cloneDeep(state.connections.data);
          }

          state.connections.data = null;
        }

        if (payload) {
          // Create connections data if it doesn't exist
          if (!state.connections.data) {
            state.connections.data = {
              result: [],
              entities: {connections: {}},
              nextRequest: {
                type: CONNECTION_TYPE.RECENT,
                meta: {
                  count: 0,
                  perPage: 0,
                  currentPage: 0,
                  totalPages: 0,
                  nextPage: null,
                  hasMore: false,
                },
              },
              total: 0,
            };
          }

          // Use Set to create unique connection IDs
          const uniqueConnectionIds = Array.from(
            new Set([
              ...(state.connections.data?.result || []),
              ...payload.result,
            ]),
          );

          // Use direct property assignments
          if (!state.connections.data.entities) {
            state.connections.data.entities = {connections: {}};
          }

          // Merge connections from payload
          const connectionsData = state.connections.data;
          if (payload.entities?.connections && connectionsData) {
            if (!connectionsData.entities) {
              connectionsData.entities = {connections: {}};
            }
            Object.keys(payload.entities.connections).forEach(key => {
              if (!connectionsData.entities.connections) {
                connectionsData.entities.connections = {};
              }
              connectionsData.entities.connections[key] =
                payload.entities.connections[key];
            });
          }

          // Assign result directly
          state.connections.data.result = uniqueConnectionIds;
        }
      })
      .addCase(searchConnectionFailure, state => {
        state.searchConnection.status = SliceStatus.rejected;
      })
      .addCase(resetSearchConnection, state => {
        state.searchConnection = {
          status: SliceStatus.idle,
          keyword: '',
        };

        if (state.connectionsCopy) {
          state.connections.data = cloneDeep(state.connectionsCopy);
          state.connectionsCopy = null;
        }
      })
      .addCase(getUnreadAllMessageCount.fulfilled, (state, action) => {
        state.unreadAllMessageCount = action.payload;
      })
      .addCase(setUnreadMessageCount, (state, action) => {
        const connection =
          state.connections.data?.entities.connections[action.payload.uid];
        if (connection) {
          connection.unreadMessageCount = action.payload.value;
        }
      })
      .addCase(setParticipantId, (state, action) => {
        state.participantId = action.payload;
      })
      .addDefaultCase(state => state),
});
/**
 * reducer
 */
export const {
  reducer: messagingReducer,
  name: messagingReducerName,
  getInitialState: getMessagingInitialState,
} = messagingSlice;

/**
 * messagingActions
 */
export const messagingActions = {
  loginUserOnCometChat,
  cometChatLogOut,
  cometChatLogOutFailure,
  cometChatLogOutSuccess,
  getMessages,
  getUnreadAllMessageCount,
  addIncomingMessage,
  getConnections,
  setUnreadMessageCount,
  setLastMessage,
  setUserPresenceStatus,
  setUserTypingStatus,
  markSentMessageAsDelivered,
  markSentMessageAsRead,
  searchConnection,
  searchConnectionSuccess,
  searchConnectionFailure,
  resetSearchConnection,
  getAConnection,
  resetExternalConnection,
  markReceivedMessageAsDelivered,
  markReceivedMessageAsRead,
  addOptimisticMessage,
  updateOptimisticMessage,
  setParticipantId,
};

/**
 * TMessagingActions
 */
export type TMessagingActions =
  | AsyncThunkActionTypes<typeof loginUserOnCometChat>
  | AsyncThunkActionTypes<typeof getMessages>
  | AsyncThunkActionTypes<typeof getUnreadAllMessageCount>
  | ReturnType<typeof addIncomingMessage>
  | AsyncThunkActionTypes<typeof getConnections>
  | ReturnType<typeof cometChatLogOut>
  | ReturnType<typeof cometChatLogOutFailure>
  | ReturnType<typeof cometChatLogOutSuccess>
  | ReturnType<typeof setUnreadMessageCount>
  | ReturnType<typeof setUserPresenceStatus>
  | ReturnType<typeof setUserTypingStatus>
  | ReturnType<typeof markSentMessageAsDelivered>
  | ReturnType<typeof markSentMessageAsRead>
  | ReturnType<typeof markReceivedMessageAsDelivered>
  | ReturnType<typeof markReceivedMessageAsRead>
  | ReturnType<typeof setLastMessage>
  | ReturnType<typeof searchConnection>
  | ReturnType<typeof searchConnectionFailure>
  | ReturnType<typeof searchConnectionSuccess>
  | ReturnType<typeof resetSearchConnection>
  | AsyncThunkActionTypes<typeof getAConnection>
  | ReturnType<typeof resetExternalConnection>
  | ReturnType<typeof addOptimisticMessage>
  | ReturnType<typeof updateOptimisticMessage>
  | ReturnType<typeof setParticipantId>;

/**
 * MessagingState
 */
export type MessagingState = ReturnType<typeof messagingReducer>;
